import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import type { CreateUsuarioPayload } from '../hooks/useAdminUsuarios';
import { useAdminLookups } from '../hooks/useAdminUsuarios';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateUsuarioPayload) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function CriarUsuarioDialog({ open, onClose, onSubmit, isLoading, error }: Props) {
  const { vendedores } = useAdminLookups();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [perfil, setPerfil] = useState<CreateUsuarioPayload['perfil']>('vendedor');
  const [vendedorId, setVendedorId] = useState<number | ''>('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async () => {
    await onSubmit({
      nome,
      email,
      password,
      perfil,
      vendedor_erp_id: vendedorId === '' ? null : vendedorId
    });
    setNome('');
    setEmail('');
    setPassword('');
    setPerfil('vendedor');
    setVendedorId('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Novo Usuário</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <FormControl fullWidth>
          <InputLabel>Perfil</InputLabel>
          <Select value={perfil} label="Perfil" onChange={(e) => setPerfil(e.target.value as any)}>
            <MenuItem value="vendedor">Vendedor</MenuItem>
            <MenuItem value="gerente">Gerente</MenuItem>
            <MenuItem value="representante">Representante</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Vendedor ERP</InputLabel>
          <Select
            value={vendedorId}
            label="Vendedor ERP"
            onChange={(e) => setVendedorId(e.target.value as any)}
          >
            <MenuItem value="">Nenhum</MenuItem>
            {(vendedores.data ?? []).map((v) => (
              <MenuItem key={v.codigo} value={v.codigo}>
                {v.nome} ({v.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !nome || !email || !password}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
